import { useState, useEffect } from 'react';
import {
  Package,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { cn } from '@/lib/utils';
import { format, formatDistanceToNow } from 'date-fns';
import { useResponsive } from '@/hooks/useResponsive';
import toast from 'react-hot-toast';
import { deploymentsApi } from '@/lib/api/deployments';
import { Deployment } from '@/types/deployment';

export type BuildStatus = 'queued' | 'running' | 'success' | 'failed' | 'timeout' | 'cancelled';

interface BuildDeployHistoryTableProps {
  projectId: string;
}

const statusConfig: Record<BuildStatus, { icon: typeof CheckCircle; color: string; label: string }> = {
  queued: { icon: Clock, color: 'text-muted-foreground', label: 'Queued' },
  running: { icon: Loader2, color: 'text-blue-500', label: 'Running' },
  success: { icon: CheckCircle, color: 'text-green-500', label: 'Success' },
  failed: { icon: XCircle, color: 'text-destructive', label: 'Failed' },
  timeout: { icon: AlertCircle, color: 'text-yellow-500', label: 'Timeout' },
  cancelled: { icon: XCircle, color: 'text-muted-foreground', label: 'Cancelled' },
};

const formatDuration = (seconds?: number) => {
  if (seconds === undefined || seconds === null) return '-';
  if (seconds === 0) return '0s';
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (minutes === 0) return `${secs}s`;
  return `${minutes}m ${secs}s`;
};

export default function BuildDeployHistoryTable({ projectId }: BuildDeployHistoryTableProps) {
  const { isMobile } = useResponsive();
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [currentDeployment, setCurrentDeployment] = useState<Deployment | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | BuildStatus>('all');
  const [rollingBackId, setRollingBackId] = useState<string | null>(null);
  const [deploymentToRollback, setDeploymentToRollback] = useState<Deployment | null>(null);

  useEffect(() => {
    loadDeployments();
    // Refresh every 10 seconds if there are running deployments
    const interval = setInterval(() => {
      const hasRunningDeployments = deployments.some(d => d.status === 'queued' || d.status === 'running');
      if (hasRunningDeployments) {
        loadDeployments(true);
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [projectId]);

  const loadDeployments = async (silent = false) => {
    if (!silent) {
      setLoading(true);
    }

    try {
      // Load deployments and current deployment in parallel
      const [deploymentsData, currentData] = await Promise.all([
        deploymentsApi.getDeployments(projectId, 50, 'publish'),
        deploymentsApi.getCurrentDeployment(projectId)
      ]);

      setDeployments(deploymentsData);
      setCurrentDeployment(currentData);
    } catch (err: any) {
      if (!silent) {
        toast.error('Failed to load deploy history');
        console.error(err);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadDeployments();
  };

  const handleRollback = async () => {
    if (!deploymentToRollback) return;

    try {
      setRollingBackId(deploymentToRollback.id);
      setDeploymentToRollback(null);
      await deploymentsApi.rollbackToDeployment(projectId, deploymentToRollback.id);
      toast.success(`Rolled back to deployment #${deploymentToRollback.deployNumber}`);
      loadDeployments();
    } catch (err: any) {
      toast.error('Failed to rollback deployment');
      console.error(err);
    } finally {
      setRollingBackId(null);
    }
  };

  const getLiveDeployment = (): Deployment | undefined => {
    return currentDeployment || undefined;
  };

  // Deduplicate by buildId - show only the latest deployment per build
  const deduplicatedDeployments = (() => {
    const buildMap = new Map<string, Deployment>();
    for (const deployment of deployments) {
      if (!buildMap.has(deployment.buildId)) {
        buildMap.set(deployment.buildId, deployment);
      }
    }
    return Array.from(buildMap.values());
  })();

  const filteredDeployments = statusFilter === 'all'
    ? deduplicatedDeployments
    : deduplicatedDeployments.filter(d => d.status === statusFilter);

  const filters = [
    { key: 'all' as const, label: 'All', count: deduplicatedDeployments.length },
    { key: 'success' as const, label: 'Success', count: deduplicatedDeployments.filter(d => d.status === 'success').length },
    { key: 'failed' as const, label: 'Failed', count: deduplicatedDeployments.filter(d => d.status === 'failed').length },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (deployments.length === 0) {
    return (
      <div className={cn("flex flex-col items-center justify-center text-center", isMobile ? "py-12" : "py-16")}>
        <div className="rounded-full bg-muted p-3 mb-4">
          <Package className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="text-sm font-semibold mb-1">No deployments yet</h3>
        <p className="text-sm text-muted-foreground">
          Publish your project to see deploy history here
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* Filter tabs + Refresh */}
        <div
          className={cn(
            "flex items-center justify-between",
            isMobile && "flex-col gap-3"
          )}
        >
          <div className={cn(
            "inline-flex items-center rounded-lg bg-muted/50 p-1 gap-1",
            isMobile && "w-full justify-center"
          )}>
            {filters.map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                className={cn(
                  "px-3 py-1.5 text-[13px] font-medium rounded-md whitespace-nowrap transition-all",
                  statusFilter === key
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {label} ({count})
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="gap-1.5 h-8"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
            Refresh
          </Button>
        </div>

        {/* Deploy list */}
        <div className="space-y-2">
          {filteredDeployments.map((deployment) => {
            const isLive = deployment.status === 'success' && getLiveDeployment()?.id === deployment.id;
            const deployDate = typeof deployment.createdAt === 'string' ? deployment.createdAt : deployment.createdAt.toISOString();
            const config = statusConfig[deployment.status as BuildStatus] || statusConfig.queued;
            const StatusIcon = config.icon;

            return isMobile ? (
              /* Mobile card */
              <div key={deployment.id} className="rounded-lg border border-border bg-card p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <StatusIcon className={cn("h-3.5 w-3.5", config.color, deployment.status === 'running' && 'animate-spin')} />
                    <span className="font-mono text-sm font-medium">#{deployment.deployNumber}</span>
                    {isLive && (
                      <span className="text-[11px] font-medium text-green-600 dark:text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded-full">Live</span>
                    )}
                  </div>
                  <span className={cn("text-xs font-medium px-2 py-0.5 rounded-full",
                    deployment.status === 'success' && "bg-green-500/10 text-green-500",
                    deployment.status === 'failed' && "bg-destructive/10 text-destructive",
                    deployment.status === 'running' && "bg-blue-500/10 text-blue-500",
                    deployment.status === 'queued' && "bg-muted text-muted-foreground",
                    deployment.status === 'timeout' && "bg-yellow-500/10 text-yellow-500",
                    deployment.status === 'cancelled' && "bg-muted text-muted-foreground",
                  )}>{config.label}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground pl-5.5">
                  <span>{formatDistanceToNow(new Date(deployDate), { addSuffix: true })}</span>
                  <div className="flex items-center gap-3">
                    <span className="tabular-nums">{formatDuration(deployment.duration)}</span>
                    {deployment.status === 'success' && !isLive && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeploymentToRollback(deployment)}
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                        disabled={rollingBackId === deployment.id}
                      >
                        {rollingBackId === deployment.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RotateCcw className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* Desktop card */
              <div
                key={deployment.id}
                className={cn(
                  "flex items-center rounded-lg border bg-card px-4 py-3 group transition-colors hover:bg-muted/30",
                  isLive ? "border-green-500/30" : "border-border"
                )}
              >
                {/* Status icon + Deploy # + Live */}
                <div className="flex items-center gap-3 w-[180px] shrink-0">
                  <div className={cn(
                    "flex items-center justify-center h-8 w-8 rounded-full shrink-0",
                    deployment.status === 'success' && "bg-green-500/10",
                    deployment.status === 'failed' && "bg-destructive/10",
                    deployment.status === 'running' && "bg-blue-500/10",
                    deployment.status === 'queued' && "bg-muted",
                    deployment.status === 'timeout' && "bg-yellow-500/10",
                    deployment.status === 'cancelled' && "bg-muted",
                  )}>
                    <StatusIcon className={cn("h-4 w-4", config.color, deployment.status === 'running' && 'animate-spin')} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold">#{deployment.deployNumber}</span>
                    {isLive && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-green-600 dark:text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded-full">Live</span>
                    )}
                  </div>
                </div>

                {/* Status label */}
                <span className={cn(
                  "text-xs font-medium px-2.5 py-1 rounded-full w-[90px] text-center shrink-0",
                  deployment.status === 'success' && "bg-green-500/10 text-green-500",
                  deployment.status === 'failed' && "bg-destructive/10 text-destructive",
                  deployment.status === 'running' && "bg-blue-500/10 text-blue-500",
                  deployment.status === 'queued' && "bg-muted text-muted-foreground",
                  deployment.status === 'timeout' && "bg-yellow-500/10 text-yellow-500",
                  deployment.status === 'cancelled' && "bg-muted text-muted-foreground",
                )}>
                  {config.label}
                </span>

                {/* Time */}
                <div className="flex-1 min-w-0 ml-4">
                  <span className="text-sm text-foreground">
                    {formatDistanceToNow(new Date(deployDate), { addSuffix: true })}
                  </span>
                  <span className="text-xs text-muted-foreground ml-2">
                    {format(new Date(deployDate), 'MMM d, h:mm a')}
                  </span>
                </div>

                {/* Duration */}
                <span className="text-xs text-muted-foreground tabular-nums w-[60px] text-right shrink-0">
                  {formatDuration(deployment.duration)}
                </span>

                {/* Action */}
                <div className="w-[40px] shrink-0 flex justify-end">
                  {deployment.status === 'success' && !isLive && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeploymentToRollback(deployment)}
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Rollback to this deployment"
                      disabled={rollingBackId === deployment.id}
                    >
                      {rollingBackId === deployment.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RotateCcw className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Rollback Confirmation Modal */}
      {deploymentToRollback && (
        <Modal
          isOpen={!!deploymentToRollback}
          onClose={() => setDeploymentToRollback(null)}
          title="Confirm Rollback"
          size="md"
        >
          <div className="space-y-4">
            <p className="text-foreground">
              Are you sure you want to rollback to deployment #{deploymentToRollback.deployNumber}?
            </p>

            <div className="space-y-2 text-sm text-muted-foreground">
              <p>This will:</p>
              <ul className="space-y-1.5 ml-1">
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>Switch your live app to this deployment</span>
                </li>
                <li className="flex items-start gap-2">
                  <span>•</span>
                  <span>Reset your editor code to that commit</span>
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Deploy</span>
                <span className="font-mono text-sm font-semibold">#{deploymentToRollback.deployNumber}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Created</span>
                <span className="text-sm">{format(new Date(typeof deploymentToRollback.createdAt === 'string' ? deploymentToRollback.createdAt : deploymentToRollback.createdAt.toISOString()), 'MMM d, h:mm a')}</span>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setDeploymentToRollback(null)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRollback}
                className="flex-1"
                disabled={rollingBackId === deploymentToRollback.id}
              >
                {rollingBackId === deploymentToRollback.id ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Rolling back...
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Confirm Rollback
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
